import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react'
import { RootState } from '../index'

export interface DataRange {
  firstDate: string | null
  lastDate: string | null
}

export interface SummaryData {
  totalVisitors: number
  registeredUsers: number
  projectsCreated: number
  creditsPurchasedUsers: number
  seatsPurchasedUsers: number
  avgDurationSeconds: number | null
  medianDurationSeconds: number | null
  registrationRate: number
  projectCreationRate: number
  creditConversionRate: number
  totalActivityEvents: number
  avgActionsPerSession: number
}

export interface HourlyTrafficRow {
  hour: number
  visitor_count: number
}

export interface DailyTrendRow {
  date: string
  visitor_count: number
}

export interface WeekdayRow {
  weekday: number
  weekday_name: string
  visitor_count: number
}

export interface DeviceRow {
  device: string
  count: number
}

export interface SourceRow {
  source: string
  count: number
}

export interface OriginRow {
  origin: string
  count: number
}

export interface ActionStatRow {
  action: string
  label: string
  total_occurrences: number
  unique_users: number
  avg_seconds_to_next: number | null
}

export interface StepRow {
  action: string
  label: string
  session_count: number
  avg_seconds_from_start: number | null
}

export interface DurationBucket {
  bucket: string
  user_count: number
  sort_order: number
}

export interface LocationRow {
  country: string
  count: number
}

export interface FeedbackRatingRow {
  rating: string
  count: number
}

export interface TrialAnalyticsData {
  dataRange: DataRange
  summary: SummaryData
  hourlyTraffic: HourlyTrafficRow[]
  dailyTrend: DailyTrendRow[]
  weekdayTraffic: WeekdayRow[]
  deviceBreakdown: DeviceRow[]
  sourceBreakdown: SourceRow[]
  originBreakdown: OriginRow[]
  actionStats: ActionStatRow[]
  firstSteps: StepRow[]
  lastSteps: StepRow[]
  durationDistribution: DurationBucket[]
  locationBreakdown: LocationRow[]
  feedbackRatings: FeedbackRatingRow[]
}

export const trialAnalyticsApi = createApi({
  reducerPath: 'trialAnalyticsApi',
  baseQuery: fetchBaseQuery({
    baseUrl: '/api/v1/admin',
    prepareHeaders: (headers, { getState }) => {
      const token = (getState() as RootState).auth.token
      if (token) {
        headers.set('authorization', `Bearer ${token}`)
      }
      return headers
    },
  }),
  tagTypes: ['TrialAnalytics'],
  endpoints: (builder) => ({
    getTrialAnalytics: builder.query<
      { data: TrialAnalyticsData },
      { dateFrom?: string; dateTo?: string }
    >({
      query: (params) => ({
        url: '/trial-analytics',
        params,
      }),
      providesTags: ['TrialAnalytics'],
    }),
  }),
})

export const { useGetTrialAnalyticsQuery } = trialAnalyticsApi
